import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../../components/layout/Layout';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg';
import { 
  ClassCardSkeleton, 
  CoffeeChatCardSkeleton, 
  ArchiveCardSkeleton, 
  CommunityCardSkeleton 
} from '../../components/common/SkeletonLoaders';
import { 
  searchAll, 
  SearchResults as ApiSearchResults,
  LectureSearchItem,
  LecturerSearchItem,
  PostSearchItem 
} from '../../services/searchApi';

// 탭 타입 정의
type TabType = 'all' | 'class' | 'coffee-chat' | 'archive' | 'community';

// 변환된 검색 결과 데이터 타입 정의
interface SearchResultItem {
  id: number;
  title: string;
  caption?: string;
  content?: string;
  thumbnailUrl?: string;
  author?: string;
  date?: string;
  views?: number;
  replies?: number;
  category?: string;
  parentCategory?: string;
  tags?: string[];
  extraCaption?: string;
}

interface SearchResults {
  class: SearchResultItem[];
  coffeeChat: SearchResultItem[];
  archive: SearchResultItem[];
  community: SearchResultItem[];
}

const SearchIndex: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [inputValue, setInputValue] = useState(searchParams.get('q') || ''); // 입력 필드 값
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || ''); // 실제 검색어
  const [searchResults, setSearchResults] = useState<SearchResults>({
    class: [],
    coffeeChat: [],
    archive: [],
    community: []
  });
  const [isLoading, setIsLoading] = useState(false);

  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});

  // API 응답을 UI 형태로 변환하는 함수들
  const convertLectureToSearchItem = (lecture: LectureSearchItem): SearchResultItem => ({
    id: lecture.lecture_info.id,
    title: lecture.lecture_info.title,
    caption: lecture.lecture_info.caption,
    thumbnailUrl: lecture.lecture_info.thumbnail_url,
    tags: lecture.lecture_info.tags,
    extraCaption: `${lecture.lecturer_info.name} · ${lecture.lecturer_info.introduction}`
  });

  const convertLecturerToSearchItem = (lecturer: LecturerSearchItem): SearchResultItem => ({
    id: lecturer.id,
    title: lecturer.name,
    caption: lecturer.introduction,
    thumbnailUrl: lecturer.image_url,
    extraCaption: lecturer.introduction
  });

  const convertPostToSearchItem = (post: PostSearchItem): SearchResultItem => ({
    id: post.id,
    title: post.title,
    content: post.htmlcontent.replace(/<[^>]*>/g, ''), // HTML 태그 제거
    parentCategory: post.category,
    category: post.studyCategoryName || '일반',
    author: post.writerName,
    date: new Date(post.postDate).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    replies: 0, // API에서 제공되지 않아 기본값
    views: 0 // API에서 제공되지 않아 기본값
  });

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  // 이미지 에러 핸들러
  const handleImageError = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: false
    }));
  };

  const handleImageErrorDefault = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = 'https://placehold.co/282x188?text=No+Image';
  };

  const handleAvatarError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/images/default-avatar.png';
  };

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      setInputValue(query);
      performSearch(query);
    }
  }, [searchParams]);

  const performSearch = async (query: string) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    try {
      const apiResults: ApiSearchResults = await searchAll(query);
      
      // API 응답을 UI 형태로 변환
      const convertedResults: SearchResults = {
        class: apiResults.lectures.map(convertLectureToSearchItem),
        coffeeChat: apiResults.lecturers.map(convertLecturerToSearchItem),
        archive: apiResults.archives, // 이미 올바른 형태
        community: apiResults.posts.map(convertPostToSearchItem)
      };
      
      setSearchResults(convertedResults);
    } catch (error) {
      console.error('Search failed:', error);
      // 에러 발생 시 빈 결과로 설정
      setSearchResults({
        class: [],
        coffeeChat: [],
        archive: [],
        community: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setInputValue(query);
    setSearchParams({ q: query });
    performSearch(query);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleClearInput = () => {
    setInputValue(''); // 입력 필드만 비우기
    console.log('After clear - searchQuery:', searchQuery, 'hasResults:', hasResults()); // 디버깅
    // searchQuery와 searchResults는 유지하여 검색 결과를 보여준 상태로 유지
  };

  const getTabCounts = () => {
    return {
      class: searchResults.class.length,
      coffeeChat: searchResults.coffeeChat.length,
      archive: searchResults.archive.length,
      community: searchResults.community.length
    };
  };

  const hasResults = () => {
    const counts = getTabCounts();
    return counts.class > 0 || counts.coffeeChat > 0 || counts.community > 0;
  };

  const getFilteredResults = () => {
    if (activeTab === 'all') {
      return searchResults;
    }
    
    switch (activeTab) {
      case 'class':
        return { ...searchResults, coffeeChat: [], archive: [], community: [] };
      case 'coffee-chat':
        return { ...searchResults, class: [], archive: [], community: [] };
      case 'archive':
        return { ...searchResults, class: [], coffeeChat: [], community: [] };
      case 'community':
        return { ...searchResults, class: [], coffeeChat: [], archive: [] };
      default:
        return searchResults;
    }
  };

  return (
    <Layout>
      <Container>
        {/* 검색 헤더 */}
        <SearchHeader>
          <SearchInputContainer>
            <SearchIcon />
            <SearchInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(inputValue);
                }
              }}
              placeholder="검색어를 입력해주세요."
            />
            {inputValue && (
              <ClearButton onClick={handleClearInput}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M1.92573 11.0003C1.92573 5.98831 5.98873 1.92529 11.0007 1.92529C16.0127 1.92529 20.0757 5.98831 20.0757 11.0003C20.0757 16.0123 16.0127 20.0753 11.0007 20.0753C5.98873 20.0753 1.92573 16.0123 1.92573 11.0003ZM8.37572 7.20862C8.05354 6.88644 7.53118 6.88644 7.209 7.20862C6.88682 7.5308 6.88682 8.05316 7.209 8.37535L9.83396 11.0003L7.209 13.6253C6.88682 13.9475 6.88682 14.4698 7.209 14.792C7.53118 15.1142 8.05354 15.1142 8.37572 14.792L11.0007 12.167L13.6256 14.792C13.9478 15.1142 14.4702 15.1142 14.7924 14.792C15.1145 14.4698 15.1145 13.9475 14.7924 13.6253L12.1674 11.0003L14.7924 8.37535C15.1145 8.05316 15.1145 7.5308 14.7924 7.20862C14.4702 6.88644 13.9478 6.88644 13.6256 7.20862L11.0007 9.83359L8.37572 7.20862Z" fill="#37383C" fillOpacity="0.28"/>
                </svg>
              </ClearButton>
            )}
          </SearchInputContainer>
        </SearchHeader>

        {/* 검색 결과 제목 */}
        <SearchResultTitle>'{searchQuery || ''}' 검색 결과</SearchResultTitle>

        {/* 탭 네비게이션 */}
        <TabContainer>
          <TabList>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => handleTabChange('all')}
            >
              전체
            </Tab>
            <Tab 
              active={activeTab === 'class'} 
              onClick={() => handleTabChange('class')}
            >
              클래스
            </Tab>
            <Tab 
              active={activeTab === 'coffee-chat'} 
              onClick={() => handleTabChange('coffee-chat')}
            >
              커피챗
            </Tab>
            {/* <Tab 
              active={activeTab === 'archive'} 
              onClick={() => handleTabChange('archive')}
            >
              아카이브
            </Tab> */}
            <Tab 
              active={activeTab === 'community'} 
              onClick={() => handleTabChange('community')}
            >
              커뮤니티
            </Tab>
          </TabList>
        </TabContainer>

        {/* 검색 결과 컨텐츠 */}
        {isLoading ? (
          <ResultsContainer>
            {/* 로딩 중일 때 스켈레톤 UI 표시 */}
            {(activeTab === 'all' || activeTab === 'class') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>클래스</SectionTitle>
                </SectionHeader>
                <ClassGrid>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <ClassCard key={`class-skeleton-${index}`}>
                      <ClassThumbnail>
                        <ClassImageSkeleton />
                      </ClassThumbnail>
                      <ClassInfo>
                        <TagSkeleton />
                        <ClassTitleSkeleton />
                        <ClassExtraCaptionSkeleton />
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'coffee-chat') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커피챗</SectionTitle>
                </SectionHeader>
                <ClassGrid>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <ClassCard key={`coffee-skeleton-${index}`}>
                      <ClassThumbnail>
                        <ClassImageSkeleton />
                      </ClassThumbnail>
                      <ClassInfo>
                        <TagSkeleton />
                        <ClassTitleSkeleton />
                        <ClassExtraCaptionSkeleton />
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'archive') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>아카이브</SectionTitle>
                </SectionHeader>
                <ArchiveGrid>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <ArchiveCardSkeleton key={`archive-skeleton-${index}`} />
                  ))}
                </ArchiveGrid>
              </Section>
            )}

            {(activeTab === 'all' || activeTab === 'community') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커뮤니티</SectionTitle>
                </SectionHeader>
                <CommunityList>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <CommunityCardSkeleton key={`community-skeleton-${index}`} />
                  ))}
                </CommunityList>
              </Section>
            )}
          </ResultsContainer>
        ) : hasResults() ? (
          <ResultsContainer>
            {/* 클래스 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'class') && searchResults.class.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>클래스</SectionTitle>
                  <SectionCount>{searchResults.class.length}</SectionCount>
                </SectionHeader>
                <ClassGrid>
                  {(activeTab === 'class' ? searchResults.class : searchResults.class.slice(0, 4)).map((item) => (
                    <ClassCard key={item.id} onClick={() => navigate(`/class/lecture/${item.id}`)}>
                      <ClassThumbnail>
                        {!imageLoadStates[`class-${item.id}`] && (
                          <ClassImageSkeleton />
                        )}
                        <ClassImage 
                          src={item.thumbnailUrl} 
                          alt={item.title}
                          style={{ 
                            display: imageLoadStates[`class-${item.id}`] ? 'block' : 'none' 
                          }}
                          onLoad={() => handleImageLoad(`class-${item.id}`)}
                          onError={handleImageErrorDefault}
                        />
                      </ClassThumbnail>
                      <ClassInfo>
                        {/* 태그 표시 */}
                        {item.tags && item.tags.length > 0 && (
                          <TagContainer>
                            {item.tags.slice(0, 3).map((tag, index) => (
                              <Tag key={index}>{tag}</Tag>
                            ))}
                          </TagContainer>
                        )}
                        <ClassTitle>{item.title}</ClassTitle>
                        <ClassExtraCaption>{item.extraCaption}</ClassExtraCaption>
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
                {activeTab === 'all' && searchResults.class.length > 4 && (
                  <ViewAllButton onClick={() => handleTabChange('class')}>
                    <span>클래스 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}

            {/* 커피챗 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'coffee-chat') && searchResults.coffeeChat.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커피챗</SectionTitle>
                  <SectionCount>{searchResults.coffeeChat.length}</SectionCount>
                </SectionHeader>
                <ClassGrid>
                  {(activeTab === 'coffee-chat' ? searchResults.coffeeChat : searchResults.coffeeChat.slice(0, 4)).map((item) => (
                    <ClassCard key={item.id} onClick={() => navigate(`/coffee-chat/${item.id}`)}>
                      <ClassThumbnail>
                        {!imageLoadStates[`coffee-${item.id}`] && (
                          <ClassImageSkeleton />
                        )}
                        <ClassImage 
                          src={item.thumbnailUrl} 
                          alt={item.title}
                          style={{ 
                            display: imageLoadStates[`coffee-${item.id}`] ? 'block' : 'none' 
                          }}
                          onLoad={() => handleImageLoad(`coffee-${item.id}`)}
                          onError={handleImageErrorDefault}
                        />
                      </ClassThumbnail>
                      <ClassInfo>
                        <ClassTitle>{item.title}</ClassTitle>
                        <ClassExtraCaption>{item.extraCaption}</ClassExtraCaption>
                      </ClassInfo>
                    </ClassCard>
                  ))}
                </ClassGrid>
                {activeTab === 'all' && searchResults.coffeeChat.length > 4 && (
                  <ViewAllButton onClick={() => handleTabChange('coffee-chat')}>
                    <span>커피챗 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}

            {/* 아카이브 섹션 - 구현 전이므로 주석처리 */}
            {/* {(activeTab === 'all' || activeTab === 'archive') && (
              <Section>
                <SectionHeader>
                  <SectionTitle>아카이브</SectionTitle>
                  {searchResults.archive.length > 0 && (
                    <SectionCount>{searchResults.archive.length}</SectionCount>
                  )}
                </SectionHeader>
                {searchResults.archive.length > 0 ? (
                <ArchiveGrid>
                  {searchResults.archive.map((item) => (
                    <ArchiveCard key={item.id} onClick={() => navigate(`/archive/${item.id}`)}>
                      <ArchiveContent>
                        <ArchiveTitle>{item.title}</ArchiveTitle>
                        <ArchiveAuthor>
                          <ArchiveAvatar 
                            src="/images/default-avatar.png" 
                            alt="avatar"
                            onError={handleAvatarError}
                          />
                          <span>{item.author}</span>
                          <Divider />
                          <span>{item.date}</span>
                        </ArchiveAuthor>
                      </ArchiveContent>
                    </ArchiveCard>
                  ))}
                </ArchiveGrid>
                ) : (
                  <EmptyState>
                    <EmptyStateIcon>
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </EmptyStateIcon>
                    <EmptyStateTitle>아카이브 검색 결과가 없습니다</EmptyStateTitle>
                    <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 아카이브를 확인해보세요.</EmptyStateDescription>
                  </EmptyState>
                )}
                {activeTab === 'all' && searchResults.archive.length > 0 && (
                  <ViewAllButton onClick={() => handleTabChange('archive')}>
                    <span>아카이브 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )} */}

            {/* 커뮤니티 섹션 - 결과가 있을 때만 표시 */}
            {(activeTab === 'all' || activeTab === 'community') && searchResults.community.length > 0 && (
              <Section>
                <SectionHeader>
                  <SectionTitle>커뮤니티</SectionTitle>
                  <SectionCount>{searchResults.community.length}</SectionCount>
                </SectionHeader>
                <CommunityList>
                  {(activeTab === 'community' ? searchResults.community : searchResults.community.slice(0, 2)).map((item) => (
                    <CommunityCard key={item.id} onClick={() => { console.log("item@#@#", item); 
                    navigate(`/community/post/${item.id}`)}}>
                      <CommunityContent>
                        <CommunityMain>
                          <CommunityTitle>{item.title}</CommunityTitle>
                          <CommunityBody>{item.content}</CommunityBody>
                          <CommunityCategories>
                            <span>{item.parentCategory}</span>
                            <Divider />
                            <span>{item.category}</span>
                          </CommunityCategories>
                        </CommunityMain>
                        <CommunityMeta>
                          <CommunityAuthor>
                            <ArchiveAvatar 
                              src="/images/default-avatar.png" 
                              alt="avatar"
                              onError={handleAvatarError}
                            />
                            <span>{item.author}</span>
                            <Divider />
                            <span>{item.date}</span>
                          </CommunityAuthor>
                          <CommunityStats>
                            <span>답변 {item.replies}</span>
                            <Divider />
                            <span>조회수 {item.views}</span>
                          </CommunityStats>
                        </CommunityMeta>
                      </CommunityContent>
                    </CommunityCard>
                  ))}
                </CommunityList>
                {activeTab === 'all' && searchResults.community.length > 0 && (
                  <ViewAllButton onClick={() => handleTabChange('community')}>
                    <span>커뮤니티 전체보기</span>
                    <ChevronIcon>
                      <img src={ChevronRightIcon} alt="더보기" />
                    </ChevronIcon>
                  </ViewAllButton>
                )}
              </Section>
            )}
            
            {/* 특정 탭에서 해당 카테고리에 결과가 없을 때 빈 상태 메시지 */}
            {activeTab === 'class' && searchResults.class.length === 0 && (
              <EmptyState>
                <EmptyStateIcon>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L3 7L12 12L21 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M3 17L12 22L21 17" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M3 12L12 17L21 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </EmptyStateIcon>
                <EmptyStateTitle>클래스 검색 결과가 없습니다</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 클래스를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'coffee-chat' && searchResults.coffeeChat.length === 0 && (
              <EmptyState>
                <EmptyStateIcon>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M7 18C7 16.3431 8.34315 15 10 15H14C15.6569 15 17 16.3431 17 18V19C17 20.1046 16.1046 21 15 21H9C7.89543 21 7 20.1046 7 19V18Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </EmptyStateIcon>
                <EmptyStateTitle>커피챗 검색 결과가 없습니다</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 커피챗을 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'archive' && searchResults.archive.length === 0 && (
              <EmptyState>
                <EmptyStateIcon>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </EmptyStateIcon>
                <EmptyStateTitle>아카이브 검색 결과가 없습니다</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 아카이브를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
            
            {activeTab === 'community' && searchResults.community.length === 0 && (
              <EmptyState>
                <EmptyStateIcon>
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M8 10.5H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 14.5H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M17 7.82843V16.1716C17 16.702 16.7893 17.2107 16.4142 17.5858C16.0391 17.9609 15.5304 18.1716 15 18.1716H5C4.46957 18.1716 3.96086 17.9609 3.58579 17.5858C3.21071 17.2107 3 16.702 3 16.1716V7.82843C3 7.29799 3.21071 6.78929 3.58579 6.41421C3.96086 6.03914 4.46957 5.82843 5 5.82843H15C15.5304 5.82843 16.0391 6.03914 16.4142 6.41421C16.7893 6.78929 17 7.29799 17 7.82843Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M17 7.82843L13.5 10.5L17 13.1716V7.82843Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </EmptyStateIcon>
                <EmptyStateTitle>커뮤니티 검색 결과가 없습니다</EmptyStateTitle>
                <EmptyStateDescription>다른 키워드로 검색해보시거나 전체 커뮤니티를 확인해보세요.</EmptyStateDescription>
              </EmptyState>
            )}
          </ResultsContainer>
        ) : searchQuery ? (
          <NoResultsContainer>
            <NoResultsTitle>'{searchQuery}'에 대한 검색 결과가 없습니다.</NoResultsTitle>
            <NoResultsSubtitle>다른 검색어로 다시 검색해 보세요.</NoResultsSubtitle>
          </NoResultsContainer>
        ) : null}
      </Container>
    </Layout>
  );
};

export default SearchIndex;
// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  padding-top: 64px;
  padding-bottom: 128px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
`;

const SearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const SearchInputContainer = styled.div`
  width: 588px;
  padding: 12px;
  background: rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  backdrop-filter: blur(32px);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 20px;
  height: 20px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &::after {
    content: '';
    width: 16.08px;
    height: 16.08px;
    background: var(--Label-Normal, #171719);
    mask: url("data:image/svg+xml,%3csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e") no-repeat center;
    mask-size: contain;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;

  &::placeholder {
    color: var(--Label-Normal, #171719);
  }
`;

const ClearButton = styled.button`
  width: 22px;
  height: 22px;
  padding: 1px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  
  svg {
    width: 22px;
    height: 22px;
  }
`;

const SearchResultTitle = styled.h1`
  color: var(--Label-Normal, #171719);
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
`;

const TabContainer = styled.div`
  width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TabList = styled.div`
  width: 100%;
  height: 48px;
  background: var(--Background-Normal-Normal, white);
  border-bottom: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  display: flex;
  align-items: center;
  gap: 32px;
`;

const Tab = styled.button<{ active: boolean }>`
  height: 48px;
  padding: 12px 0;
  border: none;
  background: transparent;
  color: ${props => props.active ? 'var(--Primary-Strong, #296768)' : 'var(--Label-Assistive, rgba(55, 56, 60, 0.28))'};
  font-size: 17px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  position: relative;

  ${props => props.active && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--Primary-Strong, #296768);
    }
  `}
`;

const NoResultsContainer = styled.div`
  padding-top: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const NoResultsTitle = styled.div`
  color: var(--Label-Strong, black);
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
`;

const NoResultsSubtitle = styled.div`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ResultsContainer = styled.div`
  width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 48px;
`;

const Section = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  color: var(--Label-Strong, black);
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30.01px;
`;

const SectionCount = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 30.01px;
`;

// 새로운 카드 스타일 - ClassIndex 기반
const ClassGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;
  }
`;

const ClassCard = styled.div`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  display: inline-flex;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 8px;
  padding: 8px;
  margin: -8px;
  
  &:hover {
    background-color: rgba(41, 103, 104, 0.04);
  }
`;

const ClassThumbnail = styled.div`
  align-self: stretch;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  border: 1px solid rgba(112, 115, 124, 0.08);
  aspect-ratio: 3/2;
  min-height: 188px; /* 최소 높이 보장 */
`;

const ClassImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  transition: transform 0.3s ease;
  
  ${ClassCard}:hover & {
    transform: scale(1.05);
  }
`;

const ClassInfo = styled.div`
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: flex;
`;

const ClassTitle = styled.div`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const ClassExtraCaption = styled.div`
  align-self: stretch;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
`;

// 태그 컨테이너와 태그 스타일
const TagContainer = styled.div`
  align-self: stretch;
  overflow: hidden;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  display: inline-flex;
  margin-bottom: 4px;
`;

const Tag = styled.div`
  height: 20px;
  padding-left: 6px;
  padding-right: 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  display: flex;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
  word-wrap: break-word;
`;

const ViewAllButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid var(--Primary-Normal, #448181);
  background: transparent;
  color: var(--Primary-Normal, #448181);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChevronIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 20px;
    height: 20px;
  }
`;

const ArchiveGrid = styled.div`
  width: 100%;
  display: flex;
  gap: 24px;
`;

const ArchiveCard = styled.div`
  flex: 1;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(41, 103, 104, 0.04);
  }
`;

const ArchiveContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ArchiveTitle = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
`;

const ArchiveAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 20.01px;
    letter-spacing: 0.20px;
    
    &:last-child {
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-weight: 400;
    }
  }
`;

const ArchiveAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const CommunityList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const CommunityCard = styled.div`
  width: 100%;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(41, 103, 104, 0.04);
  }
`;

const CommunityContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CommunityMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommunityTitle = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const CommunityBody = styled.div`
  min-height: 44px;
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
`;

const CommunityCategories = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 20.01px;
    letter-spacing: 0.20px;
  }
`;

const CommunityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommunityAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 600;
    line-height: 20.01px;
    letter-spacing: 0.20px;
    
    &:last-child {
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-weight: 400;
    }
  }
`;

const CommunityStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
    font-size: 14px;
    font-family: 'Pretendard JP', sans-serif;
    font-weight: 400;
    line-height: 20.01px;
    letter-spacing: 0.20px;
  }
`;

// 스켈레톤 UI 컴포넌트들
const ClassImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassTitleSkeleton = styled.div`
  width: 80%;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ClassExtraCaptionSkeleton = styled.div`
  width: 60%;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-top: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const TagSkeleton = styled.div`
  width: 50px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 빈 상태 메시지 컴포넌트
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
  
  svg {
    width: 100%;
    height: 100%;
    fill: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  }
`;

const EmptyStateTitle = styled.h3`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;
